import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { useRequirementDocumentationStore } from '@/frontend/store/RequirementDocumentationStore';
import { I18n } from '@/i18n/I18n';
import { useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { FileDragAndDrop } from '../files/FileDragAndDrop';
import { TermsAndConditionsNotice } from '../TermsAndConditionsNotice';

interface CreateEvidenceSubmissionModalProps {
  onHide: () => void;
  category: RequirementEvidenceCategory;
  projectId: string
}

export function CreateSubmissionModal({
  onHide,
  category,
  projectId,
}: CreateEvidenceSubmissionModalProps) {
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [selectedEvidenceItemId, setSelectedEvidenceItemId] = useState<string>('');
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createSubmission = useRequirementDocumentationStore((s) => s.createSubmission);
  // projectEvidenceItems, not evidenceItems: the latter also holds other orgs' files, which we can
  // render but must never offer for attaching.
  const evidenceItems = useRequirementDocumentationStore(useShallow((s) => s.projectEvidenceItems.filter((i) => i.projectId === projectId && i.type === category.type)));

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setFile(undefined);
    setSelectedEvidenceItemId('');
    setFileError('');
    onHide();
  };

  const handleFileChange = (newFile: File | undefined) => {
    setFile(newFile);
    setSelectedEvidenceItemId('');
    setFileError('');
  };

  const handleSelectExistingItem = (itemId: string) => {
    setSelectedEvidenceItemId(itemId);
    setFile(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileError('');
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }

    if (!file && !selectedEvidenceItemId) {
      setFileError(I18n.get('fileOrExistingRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      await createSubmission({
        requirementId: category.requirementId,
        evidenceCategoryId: category.id,
        title: title.trim(),
        description: description.trim() || '',
        projectId,
        fileName: file?.name ?? '',
        type: category.type,
        evidenceItemId: selectedEvidenceItemId,
      }, setSubmissionProgress, file);
      handleClose();
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress(0);
    }
  };

  return (
    <Modal show onHide={handleClose}>
      <Modal.Body>
        <Form>
          <Modal.Title className='pb-3'>{I18n.get('attachType', { type: I18n.get(category.type) })}</Modal.Title>
          <Form.Label className="form-label-neutral">{I18n.get('typeCategory', { type: I18n.get(category.type) })}</Form.Label>
          <Form.Control
            className="form-control-neutral mb-3"
            type="text"
            value={category.name}
            disabled
            aria-label={I18n.get('evidenceCategory')}
          />
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
            <Form.Control
              className="form-control-neutral"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError('');
              }}
              isInvalid={!!titleError}
              aria-label={I18n.get('title')}
            />
            <Form.Control.Feedback type="invalid"> {titleError} </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('description')} ({I18n.get('optional')})</Form.Label>
            <Form.Control
              className="form-control-neutral"
              placeholder={I18n.get('submissionDescriptionPlaceholder', { type: I18n.get(category.type).toLowerCase() })}
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-label={I18n.get('description')}
            />
          </Form.Group>
          <FileDragAndDrop 
            file={file} 
            setFile={handleFileChange} 
            uploading={isSubmitting} 
            uploadProgress={submissionProgress} 
          />
          {evidenceItems.length > 0 && (
            <>
              <div className="d-flex align-items-center gap-2 my-3">
                <hr className="flex-grow-1 m-0" />
                <span className="text-neutral-600 small fw-bold">{I18n.get('or')}</span>
                <hr className="flex-grow-1 m-0" />
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="form-label-neutral">{I18n.get('selectExistingFile')}</Form.Label>
                <Form.Select
                  className="form-control-neutral"
                  value={selectedEvidenceItemId}
                  onChange={(e) => handleSelectExistingItem(e.target.value)}
                  aria-label={I18n.get('selectExistingFile')}
                  disabled={isSubmitting}
                >
                  <option value="">—</option>
                  {evidenceItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.file.fileName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}

          {fileError && <div className="text-danger small mt-1">{fileError}</div>}

          <TermsAndConditionsNotice description={I18n.get('attachEvidenceTermsAndConditions')} />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn-blue-light fusa-btn-medium" onClick={handleClose} disabled={isSubmitting}>
          {I18n.get('cancel')}
        </Button>
        <Button className="fusa-btn-medium fusa-btn-primary" onClick={() => void handleSave()} disabled={isSubmitting}>
          {isSubmitting ? I18n.get('saving') : I18n.get('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
